import { AlertCircle, AlertTriangle, Loader2 } from "lucide-react";
import { useDeleteUser, useUserDetails } from "@/hooks";
import { Button } from "@/components/ui/button";
import { DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DialogContent } from "@/components/ui/dialog";
import { DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Dialog } from "@/components/ui/dialog";
import { AlertDescription } from "@/components/ui/alert";
import { AlertTitle } from "@/components/ui/alert";
import { Alert } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router";

const DeleteAccountPage = () => {
  const { data: user } = useUserDetails();
  const { mutate: deleteUser, isPending } = useDeleteUser();

  if (user?.type === "anonymous") {
    return (
      <div className="grid gap-3 w-full">
        <div className="flex justify-between items-end gap-6">
          <div className="flex flex-col">
            <h1 className="text-lg font-semibold">Eliminar Cuenta</h1>
            <p className="text-xs text-muted-foreground">
              Control sobre sus datos
            </p>
          </div>
        </div>
        <Separator />
        <Alert className="bg-muted/5">
          <AlertCircle className="size-4 text-muted-foreground" />
          <AlertDescription className="text-sm">
            Para eliminar su cuenta, por favor inicie sesión primero.{" "}
            <Link
              to="/inicia-sesion?redirect=eliminar-cuenta"
              className="font-medium underline underline-offset-4"
            >
              Iniciar sesión
            </Link>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="grid gap-3 w-full">
      <div className="flex justify-between items-end gap-6">
        <div className="flex flex-col">
          <h1 className="text-lg font-semibold">Eliminar Cuenta</h1>
          <p className="text-xs text-muted-foreground">
            Control sobre sus datos
          </p>
        </div>
      </div>
      <Separator />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-medium mb-2">Información importante</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Antes de eliminar su cuenta, tenga en cuenta lo siguiente:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground">
              <li>
                Todos sus datos personales serán eliminados permanentemente
              </li>
              <li>Sus proyectos y reportes serán eliminados</li>
              <li>Sus puntos y créditos se perderán</li>
              <li>Esta acción no se puede deshacer</li>
            </ul>
          </div>

          <Alert className="border-destructive bg-destructive/5 w-full text-destructive">
            <AlertTriangle className="size-4 text-destructive" />
            <AlertTitle>Peligro</AlertTitle>
            <AlertDescription className="grid w-full">
              <p>
                Esta acción eliminará su cuenta, todos sus datos y sus puntos de
                forma permanente.
              </p>
              <div className="flex justify-end w-full">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="destructive">Eliminar cuenta</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogTitle>Peligro</DialogTitle>
                    <DialogDescription>
                      Al eliminar su cuenta, todos sus datos y puntos serán
                      eliminados. Esta acción no se puede deshacer.
                    </DialogDescription>
                    <DialogFooter>
                      <Button
                        variant="destructive"
                        onClick={() => {
                          deleteUser();
                        }}
                        disabled={isPending}
                      >
                        {isPending ? (
                          <>
                            <Loader2 className="size-4 animate-spin" />
                            Eliminando...
                          </>
                        ) : (
                          "Eliminar cuenta"
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </AlertDescription>
          </Alert>
        </div>

        <div className="grid gap-6">
          <div>
            <h2 className="text-lg font-medium mb-2">App móvil Bernardino</h2>
            <p className="text-sm text-muted-foreground mb-4">
              También puede eliminar su cuenta desde nuestra aplicación móvil de
              android.
            </p>
          </div>
          <div className="flex items-center justify-center">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-center">
                <img
                  src="/favicon.svg"
                  alt="Bernardino | Captura app icon"
                  className="h-24 w-24 rounded-xl shadow-md"
                />
              </div>
              <div className="text-center">
                <h3 className="font-medium">Bernardino | Captura</h3>
                <p className="text-xs text-muted-foreground">
                  Desarrollador: Raul Escobar
                </p>
              </div>
              <div className="text-sm gap-4">
                <p>Para eliminar su cuenta desde la app móvil:</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Abra la aplicación Bernardino | Captura</li>
                  <li>Vaya a su Perfil</li>
                  <li>Seleccione "Eliminar cuenta"</li>
                  <li>Confirme la eliminación</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
